'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { useGTMReadCards } from '@/fo/api/gtm';
import { Review } from '@/fo/ui/review/catalog/base';
import { ReviewImage } from '@/fo/ui/review/gallery/base';
import { UiReviewLightboxBase } from '@/fo/ui/review/lightbox/base';

import { useFilterSyncContext } from '../../hooks/FilterSyncContext';
import { ReviewsFilter } from '../../types';
import { FilterPanel } from '../FilterPanel/FilterPanel';
import { ReviewCard } from '../ReviewCard/ReviewCard';
import { ShowMoreButton } from '../ShowMoreButton/ShowMoreButton';

import { styles } from './ReviewsList.styles';

export interface ReviewsListProps {
  reviews: Review[];
  filters: ReviewsFilter;
  onFiltersChange: (filters: ReviewsFilter) => void;
  onLoadMore: () => void;
  isShowLoadMore?: boolean;
  isLoading?: boolean;
}

export function ReviewsList({
  reviews,
  filters,
  onFiltersChange,
  onLoadMore,
  isShowLoadMore = false,
  isLoading = false
}: ReviewsListProps) {
  const slots = styles();
  const t = useTranslations();
  const gtmRef = useGTMReadCards('read_review', 'view_all', reviews.length);

  const filterRef = useRef<HTMLDivElement>(null);
  const { setContentFilterRef } = useFilterSyncContext();

  const [lightboxImageId, setLightboxImageId] = useState<string | null>(null);
  const [lightboxImages, setLightboxImages] = useState<ReviewImage[]>([]);

  const handleImageClick = (imageId: string, images: ReviewImage[]) => {
    setLightboxImageId(imageId);
    setLightboxImages(images);
  };

  useEffect(() => {
    if (filterRef.current) {
      setContentFilterRef(filterRef.current);
    }
  }, [setContentFilterRef]);

  return (
    <>
      <div className={slots.base()}>
        <div
          ref={filterRef}
          className={slots.header()}
        >
          <FilterPanel
            filters={filters}
            onFiltersChange={onFiltersChange}
          />
        </div>

        <div
          ref={gtmRef}
          className={slots.list()}
        >
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              onImageClick={handleImageClick}
            />
          ))}
        </div>

        {isShowLoadMore && (
          <ShowMoreButton
            onClick={onLoadMore}
            isLoading={isLoading}
          >
            {t('review.buttons.reviewsShowMore')}
          </ShowMoreButton>
        )}
      </div>
      <UiReviewLightboxBase
        images={lightboxImages.map((image) => ({
          id: image.id,
          src: image.url,
          alt: image.alt
        }))}
        title="Review images"
        open={!!lightboxImageId}
        onOpenChange={(open) => {
          if (!open) {
            setLightboxImageId(null);
          }
        }}
      />
    </>
  );
}
